import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '../api/types'

interface AuthState {
  token: string | null
  user: User | null
  /** Marca de tiempo (ms) en la que vence el access token. */
  expiresAt: number | null
  setSession: (token: string, user: User, expiresInSeconds: number) => void
  setUser: (user: User) => void
  clear: () => void
  isAuthenticated: () => boolean
}

/**
 * Sesión del panel.
 *
 * El token se guarda en localStorage: es lo habitual para una SPA que consume
 * una API stateless con JWT (el backend no emite cookies de sesión). A cambio,
 * el TTL del token es corto (60 min por defecto) y el backend invalida la
 * sesión en cuanto el dispositivo o el usuario dejan de estar habilitados.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      user: null,
      expiresAt: null,

      setSession: (token, user, expiresInSeconds) =>
        set({ token, user, expiresAt: Date.now() + expiresInSeconds * 1000 }),

      setUser: (user) => set({ user }),

      clear: () => set({ token: null, user: null, expiresAt: null }),

      isAuthenticated: () => {
        const { token, expiresAt } = get()
        if (!token) return false
        // Un token ya vencido se trata como sesión cerrada sin ir al servidor.
        return expiresAt === null || expiresAt > Date.now()
      },
    }),
    { name: 'asistencia.auth' },
  ),
)
